import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authFetch } from '../../../api';
import { keys } from '@/lib/queryKeys';
import { TitleOptionsCard, titleOptionsProblem, type FieldStatus } from './TitleOptionsCard';

interface IntakeField {
  field_name: string;
  display_label: string;
  field_type: string;
  status: 'mandatory' | 'optional' | 'hidden';
  display_order: number;
  options?: unknown;
}

const DEFAULT_TITLE_OPTIONS = ['Mr.', 'Mrs.', 'Ms.', 'Miss'];

const STATUS_OPTS: IntakeField['status'][] = ['mandatory', 'optional', 'hidden'];
const STATUS_COLORS: Record<string, string> = { mandatory: '#D1FAE5|#065F46', optional: '#DBEAFE|#1D4ED8', hidden: '#F3F4F6|#6B7280' };

export default function IntakeFormConfiguratorTab() {
  const [fields, setFields] = useState<IntakeField[]>([]);
  const [loading, setLoad] = useState(true);
  const [saving, setSave] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState('');
  // The Title row is edited in its own card (status + its option list) and
  // kept out of the ↑↓ list: Registration always draws it in front of the
  // name box, so its position in the list would mean nothing.
  const [title, setTitle] = useState<{ status: FieldStatus; options: string[] } | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    authFetch('/api/intake-config')
      .then((r: Response) => r.json())
      .then((data) => {
        const rows: IntakeField[] = Array.isArray(data) ? data : [];
        const titleRow = rows.find((f) => f.field_name === 'title');
        if (titleRow) {
          const opts = Array.isArray(titleRow.options) ? titleRow.options.filter((o): o is string => typeof o === 'string') : [];
          setTitle({ status: titleRow.status, options: opts.length ? opts : DEFAULT_TITLE_OPTIONS });
        }
        setFields(rows.filter((f) => f.field_name !== 'title'));
        setLoad(false);
      })
      .catch(() => setLoad(false));
  }, []);

  const setStatus = (fieldName: string, status: IntakeField['status']) => {
    setFields((prev) => prev.map((f) => (f.field_name === fieldName ? { ...f, status } : f)));
  };

  const moveField = (idx: number, dir: number) => {
    const next = [...fields];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target]!, next[idx]!];
    setFields(next.map((f, i) => ({ ...f, display_order: i })));
  };

  const titleProblem = title ? titleOptionsProblem(title.options) : null;

  const save = async () => {
    if (titleProblem) { setSaveError(titleProblem); return; }
    setSave(true); setSaveError('');
    const titleEntry = title
      ? [{ field_name: 'title', status: title.status, display_order: 0, options: title.options.map((o) => o.trim()) }]
      : [];
    const res: Response = await authFetch('/api/intake-config/bulk', {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fields: [...titleEntry, ...fields.map((f, i) => ({ field_name: f.field_name, status: f.status, display_order: i + 1 }))] }),
    }).catch(() => null as unknown as Response);
    setSave(false);
    if (!res || !res.ok) {
      const body = res ? await res.json().catch(() => ({})) : {};
      setSaveError(body.error || 'Could not save the configuration. Check the connection and try again.');
      return;
    }
    // Registration and Edit record read this through the query cache.
    queryClient.invalidateQueries({ queryKey: keys.config.intakeConfig() });
    setSaved(true); setTimeout(() => setSaved(false), 2200);
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#6B7280' }}>Loading field configuration…</div>;

  const all = title ? [...fields, { status: title.status }] : fields;
  const counts = {
    mandatory: all.filter((f) => f.status === 'mandatory').length,
    optional: all.filter((f) => f.status === 'optional').length,
    hidden: all.filter((f) => f.status === 'hidden').length,
  };

  return (
    <div className="fade-in" style={{ maxWidth: 680 }}>
      <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
        {Object.entries(counts).map(([status, cnt]) => {
          const [bg, color] = STATUS_COLORS[status]!.split('|');
          return <div key={status} style={{ padding: '4px 14px', background: bg, color, borderRadius: 20, fontSize: 12, fontWeight: 600 }}>{cnt} {status}</div>;
        })}
      </div>

      <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 16, padding: '10px 14px', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 'var(--radius)' }}>
        💡 Changes here affect the Patient Registration form in the Visit Workflow. Use ↑↓ to reorder fields.
      </div>

      {title && (
        <TitleOptionsCard
          status={title.status}
          options={title.options}
          onStatusChange={(status) => setTitle((t) => (t ? { ...t, status } : t))}
          onOptionsChange={(options) => setTitle((t) => (t ? { ...t, options } : t))}
        />
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {fields.map((field, idx) => {
          return (
            <div key={field.field_name} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: '#fff', border: '1px solid #E5E7EB', borderRadius: 'var(--radius)', transition: 'border-color .15s' }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--teal)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E5E7EB'; }}>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <button onClick={() => moveField(idx, -1)} disabled={idx === 0} style={{ background: 'none', padding: '2px 6px', color: '#9CA3AF', fontSize: 10, opacity: idx === 0 ? 0.3 : 1 }}>▲</button>
                <button onClick={() => moveField(idx, +1)} disabled={idx === fields.length - 1} style={{ background: 'none', padding: '2px 6px', color: '#9CA3AF', fontSize: 10, opacity: idx === fields.length - 1 ? 0.3 : 1 }}>▼</button>
              </div>

              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{field.display_label}</div>
                <div style={{ fontSize: 11, color: '#9CA3AF', fontFamily: 'monospace' }}>{field.field_name} · {field.field_type}</div>
              </div>

              <div style={{ display: 'flex', gap: 6 }}>
                {STATUS_OPTS.map((s) => {
                  const [sbg, sc] = STATUS_COLORS[s]!.split('|');
                  const active = field.status === s;
                  return (
                    <button key={s} onClick={() => setStatus(field.field_name, s)}
                      style={{ padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: 'pointer', background: active ? sbg : '#F3F4F6', color: active ? sc : '#9CA3AF', border: `1px solid ${active ? sc : 'transparent'}`, transition: 'all .15s' }}>
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 20, display: 'flex', gap: 10, alignItems: 'center' }}>
        <button className="btn-primary" onClick={save} disabled={saving} style={{ minWidth: 140 }}>
          {saved ? '✓ Saved!' : saving ? 'Saving…' : 'Save configuration'}
        </button>
        <span style={{ fontSize: 12, color: '#9CA3AF' }}>Changes take effect immediately for all new visits</span>
      </div>
      {saveError && <div className="mt-2.5 text-[13px] text-danger" role="alert">{saveError}</div>}
    </div>
  );
}
